/**
 * Shrinking an image before it goes to the clustering endpoint.
 *
 * `/cluster/auto` resizes to 256 and centre-crops to 224 before it extracts
 * embeddings, so sending a 4000px original changes the grouping not at all and
 * costs roughly fifty times the bytes. Downscaling here is what keeps a few
 * hundred images inside the proxy's read timeout.
 *
 * **Nothing in here may reject, and nothing may hang.** It sits in the middle
 * of a loop over every image; one throw would fail a build that was otherwise
 * fine. Every failure path returns the ORIGINAL blob, which is always a correct
 * answer — just a slower one. That is also what happens under jsdom, where
 * there is no canvas at all.
 */

export const MAX_EDGE = 256;
export const JPEG_QUALITY = 0.82;
/** A decode that never settles would stall the whole build. */
const DECODE_TIMEOUT_MS = 10000;

/**
 * Is there a usable 2D canvas here?
 *
 * Checked BEFORE any image is decoded, because jsdom provides `Image` but never
 * fires its `onload` — probing with a real decode would hang the test run
 * rather than fall back.
 */
export const canvasAvailable = () => {
  try {
    if (typeof document === 'undefined' || !document.createElement) return false;
    const canvas = document.createElement('canvas');
    if (!canvas || typeof canvas.getContext !== 'function') return false;
    if (typeof canvas.toBlob !== 'function') return false;
    return !!canvas.getContext('2d');
  } catch (error) {
    return false;
  }
};

const loadImage = (blob) =>
  new Promise((resolve, reject) => {
    let url = null;
    let timer = null;
    const done = (fn, value) => {
      if (timer) clearTimeout(timer);
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          // Already revoked; nothing to do.
        }
      }
      fn(value);
    };
    try {
      url = URL.createObjectURL(blob);
    } catch (error) {
      reject(error);
      return;
    }
    const image = new Image();
    timer = setTimeout(() => done(reject, new Error('decode timed out')), DECODE_TIMEOUT_MS);
    image.onload = () => done(resolve, image);
    image.onerror = () => done(reject, new Error('decode failed'));
    image.src = url;
  });

/** The size to draw at, preserving aspect ratio and never scaling UP. */
export const fitWithin = (width, height, maxEdge = MAX_EDGE) => {
  const w = Math.max(1, Math.round(width || 0));
  const h = Math.max(1, Math.round(height || 0));
  const longest = Math.max(w, h);
  if (!longest || longest <= maxEdge) return { width: w, height: h, scaled: false };
  const ratio = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(w * ratio)),
    height: Math.max(1, Math.round(h * ratio)),
    scaled: true,
  };
};

export const downscaleImage = async (blob, maxEdge = MAX_EDGE) => {
  if (!blob) return blob;
  if (!canvasAvailable()) return blob;

  try {
    const image = await loadImage(blob);
    const size = fitWithin(image.naturalWidth || image.width, image.naturalHeight || image.height, maxEdge);
    if (!size.scaled) return blob;

    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) return blob;
    context.drawImage(image, 0, 0, size.width, size.height);

    const shrunk = await new Promise((resolve) => {
      try {
        canvas.toBlob((result) => resolve(result), 'image/jpeg', JPEG_QUALITY);
      } catch (error) {
        resolve(null);
      }
    });
    // A larger "downscale" is not a downscale.
    if (!shrunk || !shrunk.size || shrunk.size >= blob.size) return blob;
    return shrunk;
  } catch (error) {
    return blob;
  }
};

export default downscaleImage;
